import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardMedia from '@mui/material/CardMedia';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Button from '@mui/material/Button';
import SettingsIcon from '@mui/icons-material/Settings';
import * as api from '../../api';
import Header from '../../components/Header';

export default function RegionHome({ locale }) {
  const { regionId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [region, setRegion] = useState(null);
  const [matches, setMatches] = useState(null);
  const [auth, setAuth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState(0);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.getRegion(regionId).then((r) => setRegion(r.data)),
      api.getMatches().then((r) => setMatches(r.data || {})).catch(() => setMatches({})),
      api.getRegionAuth(regionId).then((r) => setAuth(r.data)).catch(() => null),
    ]).catch((e) => setError(e.message)).finally(() => setLoading(false));
  }, [regionId]);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><CircularProgress /></Box>;
  }
  if (error) {
    return <Container sx={{ mt: 4 }}><Alert severity="error">{error}</Alert></Container>;
  }

  const now = new Date();
  const regionMatches = (Array.isArray(matches) ? matches : Object.values(matches || {}))
    .filter((m) => m.countryCode === region?.countryCode || m.region === region?.countryCode)
    .sort((a, b) => {
      const da = new Date(a.startDate.year, a.startDate.month - 1, a.startDate.day);
      const db = new Date(b.startDate.year, b.startDate.month - 1, b.startDate.day);
      return db - da;
    });

  const upcoming = regionMatches.filter((m) => {
    const d = new Date(m.startDate.year, m.startDate.month - 1, m.startDate.day);
    return d >= now;
  }).reverse(); // soonest first

  const past = regionMatches.filter((m) => {
    const d = new Date(m.startDate.year, m.startDate.month - 1, m.startDate.day);
    return d < now;
  });

  const displayed = tab === 0 ? upcoming : past;

  return (
    <>
      <Header locale={locale} />
      <Container maxWidth="lg" sx={{ mt: 3, pb: 6 }}>
        {/* Region header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {region?.logo && (
              <Box
                component="img"
                src={region.logo}
                alt=""
                sx={{ height: 60, width: 'auto', objectFit: 'contain' }}
              />
            )}
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {region?.title || regionId}
            </Typography>
          </Box>
          {auth?.permissions?.includes('manage') && (
            <Button
              variant="outlined"
              startIcon={<SettingsIcon />}
              onClick={() => navigate(`/regions/${regionId}/manage`)}
            >
              {t('common.manage', 'Manage')}
            </Button>
          )}
        </Box>

        {/* Tabs */}
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
          <Tab label={`${t('regions.upcoming', 'Upcoming')} (${upcoming.length})`} />
          <Tab label={`${t('regions.past', 'Past')} (${past.length})`} />
        </Tabs>

        {/* Match cards */}
        {displayed.length === 0 ? (
          <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            {t('regions.noMatches', 'No matches found.')}
          </Typography>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 2 }}>
            {displayed.map((match) => {
              const date = moment([match.startDate.year, match.startDate.month - 1, match.startDate.day])
                .locale(i18n.language).format('LL');
              const stageCount = (match.stages || []).length;

              return (
                <Card key={match.id} sx={{ borderRadius: 2, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                  <CardActionArea onClick={() => navigate(`/${match.alias || match.id}`)} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
                    {match.cover && (
                      <CardMedia
                        component="img"
                        height="120"
                        image={match.cover}
                        alt=""
                        sx={{ objectFit: 'cover' }}
                      />
                    )}
                    <CardContent sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {match.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {date}
                        {stageCount > 0 && ` · ${stageCount} ${t('matches.stages')}`}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              );
            })}
          </Box>
        )}
      </Container>
    </>
  );
}
